import React, { useState, useEffect } from 'react';
import mammoth from 'mammoth';
import { Loader, AlertTriangle, Download, FileText, FileSpreadsheet, Presentation } from 'lucide-react';

const DocumentViewer = ({ fileUrl, fileName, onDownload, file }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [htmlContent, setHtmlContent] = useState('');
    const [docType, setDocType] = useState('unknown');

    useEffect(() => {
        if (fileUrl && fileName) {
            loadDocument();
        }
    }, [fileUrl, fileName]);

    const getDocType = () => {
        const extension = fileName?.split('.').pop()?.toLowerCase() || '';
        if (['doc', 'docx'].includes(extension)) return 'word';
        if (['xls', 'xlsx'].includes(extension)) return 'excel';
        if (['ppt', 'pptx'].includes(extension)) return 'powerpoint';
        return 'unknown';
    };

    const loadDocument = async () => {
        try {
            setLoading(true);
            setError(null);

            const type = getDocType();
            setDocType(type);

            if (type === 'word') {
                // Fetch the file as array buffer
                const response = await fetch(fileUrl);
                if (!response.ok) throw new Error('Failed to fetch document');

                const arrayBuffer = await response.arrayBuffer();

                // Convert docx to HTML using mammoth
                const result = await mammoth.convertToHtml({ arrayBuffer });

                if (result.value) {
                    setHtmlContent(result.value);
                } else {
                    throw new Error('Could not extract content from document');
                }

                // Log any warnings
                if (result.messages && result.messages.length > 0) {
                    console.log('Mammoth warnings:', result.messages);
                }
            } else {
                // For Excel and PowerPoint, show download prompt
                setError(`Preview not available for ${type} files. Please download to view.`);
            }

            setLoading(false);
        } catch (err) {
            console.error('Error loading document:', err);
            setError(err.message || 'Failed to load document');
            setLoading(false);
        }
    };

    const getIcon = () => {
        switch (docType) {
            case 'word':
                return <FileText className="h-16 w-16 text-blue-400" />;
            case 'excel':
                return <FileSpreadsheet className="h-16 w-16 text-green-400" />;
            case 'powerpoint':
                return <Presentation className="h-16 w-16 text-orange-400" />;
            default:
                return <FileText className="h-16 w-16 text-gray-400" />;
        }
    };

    const getTypeName = () => {
        switch (docType) {
            case 'word': return 'Word Document';
            case 'excel': return 'Excel Spreadsheet';
            case 'powerpoint': return 'PowerPoint Presentation';
            default: return 'Office Document';
        }
    };

    if (loading) {
        return (
            <div className="doc-viewer-loading">
                <Loader className="h-8 w-8 animate-spin text-blue-400 mb-4" />
                <p className="text-gray-400">Loading document...</p>
            </div>
        );
    }

    if (error || !htmlContent) {
        return (
            <div className="doc-viewer-error">
                {getIcon()}
                <h3 className="text-lg font-semibold text-white mb-2 mt-4">{getTypeName()}</h3>
                <p className="text-gray-400 mb-2 text-sm">{fileName}</p>
                {error && (
                    <p className="text-gray-500 text-xs mb-4">{error}</p>
                )}
                <p className="text-gray-500 text-xs mb-4">
                    {docType === 'word'
                        ? 'Unable to preview this document. It may be in an older format or corrupted.'
                        : 'This file type requires downloading to view properly.'}
                </p>
                {onDownload && file && (
                    <button
                        onClick={() => onDownload(file)}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <Download className="h-4 w-4 mr-2" />
                        Download to View
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="doc-viewer-container">
            <div className="doc-viewer-content">
                <div
                    className="doc-viewer-html"
                    dangerouslySetInnerHTML={{ __html: htmlContent }}
                />
            </div>
        </div>
    );
};

export default DocumentViewer;
